/**
 * Component: BirdEye embed
 * Shared handling of the pasted BirdEye install codes, used by the webchat and the reviews feed.
 *
 * Both surfaces ship the same shape — a <script src> plus the <div id="bf-revz-widget-…"> the
 * bundle polls for — but the widget id sits in a different path segment, so the caller says how
 * far from the end to read it.
 *
 * Loaded as a classic script by whichever snippet needs it, immediately before its consumer:
 * deferred scripts run in document order, so the consumer always sees window.BirdeyeEmbed. Two
 * consumers on one page request this file twice; the guard below makes the second run a no-op.
 */
window.BirdeyeEmbed =
  window.BirdeyeEmbed ||
  (() => {
    const SRC_PATTERN = /src\s*=\s*["']([^"']+)["']/;
    const CONTAINER_PATTERN = /id\s*=\s*["'](bf-revz-widget-(\d+))["']/;

    /* Marks every container this theme injects, so each surface can tell its own widget from a
       different BirdEye one. An unmarked container came from somewhere else — a Custom Liquid
       paste — and is treated as unknown rather than assumed harmless. */
    const WIDGET_ATTRIBUTE = "data-birdeye-widget";

    function readJsonConfig(node, label) {
      if (!node) return null;

      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        console.error(`${label} configuration is not valid JSON, nothing is shown.`, error);
        return null;
      }
    }

    function parseEmbedCode(code, { label, source, idSegmentFromEnd }) {
      const src = code.match(SRC_PATTERN);
      const container = code.match(CONTAINER_PATTERN);

      if (!src || !container) {
        console.error(
          `${label} ${source} is not a usable widget code — it needs both the <script> tag and the <div id="bf-revz-widget-…"> after it. Nothing is injected.`,
        );
        return null;
      }

      /* Positional, hence a warning rather than a refusal: the vendor renaming a path segment
         must not black out a working surface. */
      const segments = src[1].split("/").filter(Boolean);
      const widgetIdInSrc = segments[segments.length - idSegmentFromEnd];
      if (widgetIdInSrc !== container[2]) {
        console.warn(
          `${label} ${source} may be inconsistent: the script path points at ${widgetIdInSrc} but the div is ${container[1]}. Injecting anyway — if nothing appears, re-copy the code from BirdEye.`,
        );
      }

      return { src: src[1], containerId: container[1] };
    }

    /* Container first: the vendor bundle does not create it, it polls for the div whose id
       matches the widget id baked into the script. */
    function injectWidget(target, parsed, kind) {
      const container = document.createElement("div");
      container.id = parsed.containerId;
      container.setAttribute(WIDGET_ATTRIBUTE, kind);
      target.appendChild(container);

      const script = document.createElement("script");
      script.src = parsed.src;
      script.defer = true;
      target.appendChild(script);

      return container;
    }

    return { WIDGET_ATTRIBUTE, readJsonConfig, parseEmbedCode, injectWidget };
  })();
